import { FC, useCallback } from "react";

import _isNil from "lodash/isNil";
import _startCase from "lodash/startCase";
import dynamic from "next/dynamic";
import { useRouter } from "next/router";

import { getLabel, getValue } from "components/dropdown-menu";
import Grid from "components/grid";
import Filters, { FiltersProps } from "components/grid/filters";
import PageResults from "components/grid/page-results";
import PageSize from "components/grid/page-size";
import Heading from "components/heading";
import HeroSection from "components/hero-section";
import Pagination from "components/pagination";
import CardItem from "components/views/resources/card-item";
import { ResourceItem } from "components/views/resources/types";

const DemoFormSection = dynamic(() => import("../../demo-form-section"));

const getListTitle = (filterOptions: FiltersProps["filters"], selected: FiltersProps["selected"]) => {
  const prefix =
    Object.entries(selected)
      .filter(([key, value]) => !_isNil(value))
      ?.map(([key, value]) => {
        const options = filterOptions.find(option => option.name === key)?.options;
        return getLabel(options?.find(option => getValue(option) === value));
      })
      ?.join(" & ") || "All";

  return `${prefix} Resources`;
};

export type ResourcesListProps = {
  currentPage: number | undefined;
  filters: { [key: string]: string };
  filterOptions: FiltersProps["filters"];
  items: ResourceItem[];
  pageSize: number | undefined;
  subtitle: string;
  title: string;
  totalCount: number;
};
const ResourcesList: FC<ResourcesListProps> = ({
  title,
  subtitle,
  filterOptions,
  items,
  totalCount,
  pageSize,
  currentPage,
  filters,
}: ResourcesListProps) => {
  const router = useRouter();

  const setQueryParams = useCallback(
    (newParams: { [key: string]: string | undefined }) => {
      router.push({ query: { ...router.query, ...newParams } });
    },
    [router]
  );
  const setFilters = (name: string, selected: string | undefined) => {
    setQueryParams({ [name]: selected });
  };
  const setPageSize = useCallback(
    (size: number | undefined) => {
      setQueryParams({ pageSize: size?.toString() });
    },
    [setQueryParams]
  );
  const setPage = (newPage: number) => {
    setQueryParams({ page: newPage?.toString() });
  };

  return (
    <>
      <HeroSection title={title}>
        <div className="flex-grow px-6 sm:px-10 lg:px-14 text-center">
          <div className="space-x-2 mb-4">
            <Heading tag="h2">{title}</Heading>
          </div>
          <p className="text-gray-500 text-sm md:text-base">{subtitle}</p>
        </div>
      </HeroSection>
      <Filters filters={filterOptions} selected={filters} onChange={setFilters} />
      <div className="bg-gray-100">
        <HeroSection>
          <HeroSection.Content className="px-4 sm:px-10 lg:px-14 space-y-6 text-left">
            <div className="flex gap-x-4 gap-y-1 items-center justify-between flex-wrap sm:flex-nowrap">
              <Heading tag="h5">{getListTitle(filterOptions, filters)}</Heading>
            </div>

            <Grid columns={3}>
              {items?.map(item => (
                <CardItem {...item} key={item._id} />
              ))}
            </Grid>

            <div className="pt-6 flex gap-4 items-center justify-center flex-wrap xs:justify-between">
              <div className="flex items-center gap-4 justify-between flex-wrap basis-full grow xs:grow-0 xs:basis-auto">
                {!!totalCount && <PageSize onChange={setPageSize} pageSize={pageSize} />}
                <PageResults currentPage={currentPage} totalCount={totalCount} pageSize={pageSize} />
              </div>
              <Pagination currentPage={currentPage} totalCount={totalCount} pageSize={pageSize} onChange={setPage} />
            </div>
          </HeroSection.Content>
        </HeroSection>
      </div>
      <DemoFormSection />
    </>
  );
};

export default ResourcesList;
